// ============================================================================
// CommitteeDetailPage — fiche comite (E3a, F-408..F-429) — /comites/:id
// ============================================================================
// Portage du PoC (d)/comites/[id]/page.tsx au standard nav maison (gabarit UG) :
// breadcrumb LABELLE « Comites › {nom} » (jamais de fleche grise muette) ->
// CommitteeSummaryHeader -> sections Avancement / Membres / Mises a jour +
// panneau « Modifier le comite » (collapsible ADMIN, F-411) avec suppression
// btnDangerOutline -> ConfirmDialog -> btnDanger (F-412). Detail ROUTE (URL
// /comites/{id}). Toutes les gardes sont SERVEUR (viewer.* = affordances).

import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  EmptyState, ConfirmDialog, QueryError, SkeletonLine, SkeletonCard, GS_COMPONENTS,
} from '@gs/ui';
import { ChevronRight, Inbox, MessageSquareText, ShieldAlert, X } from 'lucide-react';
import { ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  useCommitteeDetail, useUpdateCommittee, useDeleteCommittee, useAddMember,
  useRemoveMember, usePostUpdate, useDeleteUpdate, committeeErrorCode,
  type CommitteeDetailResponse, type CommitteeStatus, type CommitteeUpdate,
} from '../../hooks/useCommittees';
import { MilestonesEditor } from '../../components/comites/MilestonesEditor';
import { CommitteesForbidden } from './ComitesPage';
import { CommitteeSummaryHeader } from './CommitteeSummaryHeader';
import { formatDate } from './dates';

const INPUT_DARK = 'dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100';
const KNOWN_ERRORS = ['invalid', 'forbidden', 'notfound', 'server'];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      {children}
    </section>
  );
}

// ─── Panneau « Modifier le comite » (F-411/F-412, admin) ─────────────────────

function EditCommitteePanel({
  detail,
  onError,
}: {
  detail: CommitteeDetailResponse;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdateCommittee(detail.committee.id);
  const remove = useDeleteCommittee(detail.committee.id);

  const [name, setName] = useState(detail.committee.name);
  const [description, setDescription] = useState(detail.committee.description ?? '');
  const [status, setStatus] = useState<CommitteeStatus>(detail.committee.status);
  const [targetDate, setTargetDate] = useState(detail.committee.targetDate ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);

  function save() {
    if (!name.trim()) {
      onError('invalid');
      return;
    }
    update.mutate(
      {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        status,
        targetDate: targetDate || null,
      },
      { onError: (err) => onError(committeeErrorCode(err)) },
    );
  }

  function confirmDelete() {
    remove.mutate(undefined, {
      onSuccess: () => navigate('/comites'), // parite PoC : redirect apres delete
      onError: (err) => {
        setConfirmOpen(false);
        onError(committeeErrorCode(err));
      },
    });
  }

  return (
    <details className={`${GS_COMPONENTS.card} group px-4 py-3 dark:border-slate-700 dark:bg-slate-800`}>
      <summary className="cursor-pointer select-none text-field font-semibold text-slate-700 dark:text-slate-200">
        {t('gest:comites.edit.summary')}
      </summary>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-name" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:comites.edit.name')}
          </label>
          <input
            id="edit-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
            className={`${GS_COMPONENTS.input} ${INPUT_DARK}`}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-description" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:comites.edit.description')}
          </label>
          <textarea
            id="edit-description"
            rows={3}
            maxLength={4000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`${GS_COMPONENTS.input} ${INPUT_DARK}`}
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-status" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:comites.edit.status')}
            </label>
            {/* M-400 : statut active|paused|completed — ADMIN SEUL (ce panneau). */}
            <select
              id="edit-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as CommitteeStatus)}
              className={`${GS_COMPONENTS.input} ${INPUT_DARK} w-36`}
            >
              <option value="active">{t('gest:comites.status.active')}</option>
              <option value="paused">{t('gest:comites.status.paused')}</option>
              <option value="completed">{t('gest:comites.status.completed')}</option>
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-date" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:comites.edit.targetDate')}
            </label>
            <input
              id="edit-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className={`${GS_COMPONENTS.input} ${INPUT_DARK} w-44`}
            />
          </div>
          <button
            type="button"
            onClick={save}
            disabled={update.isPending}
            className={GS_COMPONENTS.btnAction}
          >
            {t('gest:comites.edit.save')}
          </button>
          {/* Destructif discret : btnDangerOutline -> confirmation (btnDanger). */}
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={remove.isPending}
            className={GS_COMPONENTS.btnDangerOutline}
          >
            {t('gest:comites.edit.delete')}
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title={t('gest:comites.edit.confirmTitle')}
        message={t('gest:comites.edit.confirmMessage', { name: detail.committee.name })}
        confirmLabel={t('gest:comites.edit.confirmConfirm')}
        cancelLabel={t('gest:comites.edit.confirmCancel')}
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </details>
  );
}

// ─── Membres (F-421..F-424) ──────────────────────────────────────────────────

function MembersSection({
  detail,
  onError,
}: {
  detail: CommitteeDetailResponse;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const addMember = useAddMember(detail.committee.id);
  const removeMember = useRemoveMember(detail.committee.id);
  const [selected, setSelected] = useState('');

  const isAdmin = detail.viewer.canEditCommittee;

  return (
    <Section title={t('gest:comites.sections.members', { total: detail.members.length })}>
      {detail.members.length === 0 ? (
        <EmptyState title={t('gest:comites.members.empty')} icon={<Inbox size={48} aria-hidden="true" />} />
      ) : (
        <ul className="flex flex-wrap gap-2">
          {detail.members.map((m) => {
            const label = m.fullName ?? m.email;
            return (
              <li
                key={m.email}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-3 pr-2 text-field text-slate-700 dark:bg-slate-700 dark:text-slate-200"
              >
                <span>{label}</span>
                {isAdmin ? (
                  <button
                    type="button"
                    onClick={() =>
                      removeMember.mutate(m.email, { onError: (err) => onError(committeeErrorCode(err)) })
                    }
                    disabled={removeMember.isPending}
                    aria-label={t('gest:comites.members.remove', { name: label })}
                    className="inline-flex h-4 w-4 touch-manipulation items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-200 hover:text-red-600 dark:hover:bg-slate-600 dark:hover:text-red-400"
                  >
                    <X size={12} aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {isAdmin && detail.addableUsers.length > 0 ? (
        <div className={`${GS_COMPONENTS.card} flex flex-wrap items-end gap-3 p-4 dark:border-slate-700 dark:bg-slate-800`}>
          <div className="flex min-w-48 flex-1 flex-col gap-1.5">
            <label htmlFor="add-member-user" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:comites.members.addLabel')}
            </label>
            <select
              id="add-member-user"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className={`${GS_COMPONENTS.input} ${INPUT_DARK}`}
            >
              <option value="" disabled>
                {t('gest:comites.members.addPlaceholder')}
              </option>
              {detail.addableUsers.map((u) => (
                <option key={u.email} value={u.email}>
                  {u.fullName ?? u.email}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={!selected || addMember.isPending}
            onClick={() =>
              addMember.mutate(selected, {
                onSuccess: () => setSelected(''),
                onError: (err) => onError(committeeErrorCode(err)),
              })
            }
            className={GS_COMPONENTS.btnSecondary}
          >
            {t('gest:comites.members.addSubmit')}
          </button>
        </div>
      ) : null}
    </Section>
  );
}

// ─── Mises a jour (F-425..F-428) ─────────────────────────────────────────────

function UpdateItem({
  update,
  canDelete,
  onDelete,
  deleting,
}: {
  update: CommitteeUpdate;
  canDelete: boolean;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { t, i18n } = useTranslation();
  return (
    <li className={`${GS_COMPONENTS.card} flex flex-col gap-2 p-4 dark:border-slate-700 dark:bg-slate-800`}>
      <header className="flex items-baseline justify-between gap-3">
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">
          {update.authorName ?? update.authorEmail}
        </p>
        <span className="text-meta text-slate-400 dark:text-slate-500">
          {formatDate(i18n.language, update.postedAt)}
        </span>
      </header>
      <p className="whitespace-pre-wrap text-field text-slate-700 dark:text-slate-300">{update.body}</p>
      {canDelete ? (
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className={`${GS_COMPONENTS.btnGhost} self-end`}
        >
          {t('gest:comites.updates.delete')}
        </button>
      ) : null}
    </li>
  );
}

function UpdatesSection({
  detail,
  onError,
}: {
  detail: CommitteeDetailResponse;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const post = usePostUpdate(detail.committee.id);
  const removeUpdate = useDeleteUpdate(detail.committee.id);
  const [body, setBody] = useState('');

  // M-406 : admin OU auteur — normalisation lowercase/trim (D06-06, jamais ===
  // strict). Affordance UI seulement, garde re-verifiee serveur.
  const viewerEmail = detail.viewer.email.toLowerCase().trim();
  const isAdmin = detail.viewer.canEditCommittee;

  function publish() {
    const trimmed = body.trim();
    if (!trimmed) return;
    post.mutate(trimmed, {
      onSuccess: () => setBody(''),
      onError: (err) => onError(committeeErrorCode(err)),
    });
  }

  return (
    <Section title={t('gest:comites.sections.updates', { total: detail.updates.length })}>
      {detail.viewer.canPostUpdate ? (
        // F-425 / M-403 : « Publier une mise a jour » = entree TEXTUELLE (jamais
        // « Publier le comite » — ne change ni statut ni jalons). GA-02 serveur.
        <div className={`${GS_COMPONENTS.card} flex flex-col gap-3 p-4 dark:border-slate-700 dark:bg-slate-800`}>
          <label htmlFor="post-body" className="text-field font-semibold text-slate-800 dark:text-slate-100">
            {t('gest:comites.updates.postLabel')}
          </label>
          <textarea
            id="post-body"
            rows={4}
            maxLength={4000}
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('gest:comites.updates.postPlaceholder')}
            className={`${GS_COMPONENTS.input} ${INPUT_DARK}`}
          />
          <button
            type="button"
            onClick={publish}
            disabled={post.isPending || !body.trim()}
            className={`${GS_COMPONENTS.btnAction} w-fit`}
          >
            {t('gest:comites.updates.postSubmit')}
          </button>
        </div>
      ) : null}

      {detail.updates.length === 0 ? (
        <EmptyState
          title={t('gest:comites.updates.empty')}
          icon={<MessageSquareText size={48} aria-hidden="true" />}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {detail.updates.map((u) => (
            <UpdateItem
              key={u.id}
              update={u}
              canDelete={isAdmin || u.authorEmail.toLowerCase().trim() === viewerEmail}
              deleting={removeUpdate.isPending}
              onDelete={() =>
                removeUpdate.mutate(u.id, { onError: (err) => onError(committeeErrorCode(err)) })
              }
            />
          ))}
        </ul>
      )}
    </Section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function DetailSkeleton() {
  // F-429 : skeleton fiche.
  return (
    <div className="space-y-4">
      <SkeletonLine className="h-4 w-64" />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

export default function CommitteeDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const detailQ = useCommitteeDetail(id, !!user);
  const errorCode = searchParams.get('error'); // F-410 deep-link

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next);
  }

  const notFound = detailQ.isError && detailQ.error instanceof ApiServerError && detailQ.error.status === 404;
  const forbidden = detailQ.isError && detailQ.error instanceof ApiForbiddenError;

  return (
    <div className="space-y-5">
      {/* Fil d'Ariane LABELLE « Comites › {nom} » — bloc canonique UI_STANDARDS
          §Pattern fiche (repris tel quel de apps/ug) : libelle parent cliquable,
          jamais une fleche grise muette ; identifiant courant fonce. */}
      <nav
        aria-label={t('gest:comites.breadcrumbLabel')}
        className="mb-2 flex items-center gap-1.5 text-meta text-slate-500"
      >
        <Link
          to="/comites"
          className="touch-manipulation font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
        >
          {t('gest:comites.title')}
        </Link>
        <ChevronRight size={14} aria-hidden className="text-slate-300 dark:text-slate-600" />
        <span aria-current="page" className="truncate font-semibold text-slate-800 dark:text-slate-100">
          {detailQ.data?.committee.name ?? '…'}
        </span>
      </nav>

      {errorCode ? (
        // F-410 : alerte ?error= mappee i18n (GA-21).
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {t(`gest:comites.errors.${KNOWN_ERRORS.includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {detailQ.isLoading ? (
        <DetailSkeleton />
      ) : notFound ? (
        // F-408 : id inconnu -> 404.
        <EmptyState
          icon={<ShieldAlert size={48} aria-hidden="true" />}
          title={t('gest:comites.notFound.title')}
          description={t('gest:comites.notFound.description')}
          action={
            <Link to="/comites" className={GS_COMPONENTS.btnSecondary}>
              {t('gest:comites.notFound.back')}
            </Link>
          }
        />
      ) : forbidden ? (
        <CommitteesForbidden />
      ) : detailQ.isError ? (
        <QueryError message={t('gest:comites.loadError')} onRetry={() => void detailQ.refetch()} />
      ) : detailQ.data ? (
        <>
          <CommitteeSummaryHeader
            name={detailQ.data.committee.name}
            description={detailQ.data.committee.description}
            status={detailQ.data.committee.status}
            targetDate={detailQ.data.committee.targetDate}
            createdAt={detailQ.data.committee.createdAt}
            milestonesDone={detailQ.data.milestones.filter((m) => m.done).length}
            milestonesTotal={detailQ.data.milestones.length}
            memberCount={detailQ.data.members.length}
            lastUpdateAt={detailQ.data.updates[0]?.postedAt ?? null}
          />

          {detailQ.data.viewer.canEditCommittee ? (
            <EditCommitteePanel detail={detailQ.data} onError={setError} />
          ) : null}

          <Section title={t('gest:comites.sections.milestones')}>
            <MilestonesEditor
              key={detailQ.data.committee.id}
              committeeId={detailQ.data.committee.id}
              initial={detailQ.data.milestones}
              canEdit={detailQ.data.viewer.canEditMilestones}
            />
          </Section>

          <MembersSection detail={detailQ.data} onError={setError} />
          <UpdatesSection detail={detailQ.data} onError={setError} />
        </>
      ) : null}
    </div>
  );
}
